import { c as _c } from "react/compiler-runtime";
import type { DrawForecast, DrawResult } from '@svs/nova-poolbetting/schema/forecast';
import type { BettingEvent, SportType } from '@svs/nova-poolbetting/schema/sport';
import { CouponInitMatchStatus, CouponInitResult, type EventStatusInDraw, getLabelForEvent } from '@svs/nova-poolbetting/ui/Init';
import { useGetMatchStatus } from '@svs/nova-poolbetting/ui/Init/client';
import { cn } from '@svs/nova-poolbetting/ui/utils';
import type { ReactNode } from 'react';
type CouponEventInitProps = {
  betEvent: BettingEvent;
  sportType: SportType;
  drawEventResult?: DrawForecast['drawResults'][number] | DrawResult['drawResults'][number];
  matchDrawStatus?: EventStatusInDraw;
  className?: string;
  hasMatchStarted?: boolean;
  isDrawFinished?: boolean;
};

/**
 * Presentational component for rendering the CouponEventInit and Match Status.
 *
 * @param root0 props for CouponEventInit
 * @param root0.betEvent Betting event data
 * @param root0.sportType Sport type of the draw
 * @param root0.drawEventResult Result of the event as a tuple containing home and away scores
 * @param root0.matchDrawStatus Status of the match in current draw
 * @param root0.className Optional class name for styling
 * @param root0.hasMatchStarted Indicates if the match has started, used to determine if result should be shown
 * @param root0.isDrawFinished Indicates if the draw is finished, used to determine if result should be shown
 * @returns A ReactNode that renders the CouponEventInit and Match Status.
 */
export function CouponEventInit(t0) {
  const $ = _c(25);
  const {
    betEvent,
    sportType,
    drawEventResult,
    matchDrawStatus,
    className,
    hasMatchStarted: t1,
    isDrawFinished: t2
  } = t0;
  const hasMatchStarted = t1 === undefined ? false : t1;
  const isDrawFinished = t2 === undefined ? false : t2;
  const t3 = drawEventResult ? drawEventResult.matchResult.client : "NotStarted";
  let t4;
  if ($[0] !== betEvent.startDate || $[1] !== betEvent.status || $[2] !== betEvent.statusTime || $[3] !== sportType || $[4] !== t3) {
    t4 = {
      eventStatus: betEvent.status,
      sportType,
      matchStart: betEvent.startDate,
      statusTime: betEvent.statusTime,
      matchResultState: t3
    };
    $[0] = betEvent.startDate;
    $[1] = betEvent.status;
    $[2] = betEvent.statusTime;
    $[3] = sportType;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const matchStatusText = useGetMatchStatus(t4);
  const t5 = drawEventResult?.result ? `${drawEventResult.result[0]} - ${drawEventResult.result[1]}` : undefined;
  let t6;
  if ($[6] !== betEvent.status || $[7] !== matchDrawStatus || $[8] !== matchStatusText || $[9] !== t5) {
    t6 = getLabelForEvent({
      eventResult: t5,
      matchStatusText,
      matchDrawStatus,
      sportEventStatus: betEvent.status
    });
    $[6] = betEvent.status;
    $[7] = matchDrawStatus;
    $[8] = matchStatusText;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const eventLabel = t6;
  let t7;
  if ($[11] !== className) {
    t7 = cn("flex items-center gap-1.5 text-nowrap [grid-area:init] lg:gap-2", className);
    $[11] = className;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = hasMatchStarted ? drawEventResult?.matchResult.client : "NotStarted";
  let t9;
  if ($[13] !== matchStatusText || $[14] !== t8) {
    t9 = <CouponInitMatchStatus matchStatusText={matchStatusText} matchResultState={t8} />;
    $[13] = matchStatusText;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== drawEventResult?.result || $[17] !== hasMatchStarted || $[18] !== isDrawFinished) {
    t10 = (hasMatchStarted || isDrawFinished) && <CouponInitResult result={drawEventResult?.result} variantOverride="neutral" />;
    $[16] = drawEventResult?.result;
    $[17] = hasMatchStarted;
    $[18] = isDrawFinished;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== eventLabel || $[21] !== t10 || $[22] !== t7 || $[23] !== t9) {
    t11 = <div className={t7} aria-label={eventLabel}>{t9}{t10}</div>;
    $[20] = eventLabel;
    $[21] = t10;
    $[22] = t7;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}